// Tier-1 alerts panel — the highest-decision-value surface in the Progress
// dashboard. Consumes the SessionResult list + MuscleBaseline rows +
// painMap and surfaces concrete "do something" cards: regression warnings,
// volume-spike-vs-baseline alerts, the deload suggestion. Each alert reads
// like a coach's note, not a number.
import { useEffect, useMemo, useRef, useState } from "react";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import {
  compareToBaseline,
  detectRegression,
  e1rmPointsByTrack,
  mapCatalogToCanonical,
  muscleKey,
  suggestDeload,
  toCatalogSlug,
  MIN_TOPLIFT_REGRESSION_POINTS,
  type CatalogMuscleSlug,
  type MuscleSlug,
  type PainSeverity,
  type TimedSessionResult,
} from "@jackedlogs/shared";

import { Icon } from "../Icon/Icon";
import { exerciseTrackLabel } from "../Logger/exerciseTrackLabel";
import type { UiTheme } from "../theme";
import { PanelTitleRow } from "./InfoPill";
import type { BaselineRow, ProgressApiFetch, ProgressDeps } from "./types";
import { useExerciseNameResolver } from "./useExerciseNameResolver";
import { useSessionResultsFromDeps } from "./useSessionResults";

type Severity = "alarm" | "warn" | "info";

type Alert = { id: string; severity: Severity; text: string };

// A baseline row needs this many sampled days before it is worth comparing
// against — below it, "no spike" means "we haven't watched long enough".
const BASELINE_MIN_SAMPLE_DAYS = 28;
// e1RM observations required before a downward trend counts as a trend.
const MIN_REGRESSION_POINTS = 4;

type ListResp = { items?: unknown[] };

async function fetchBaselines(
  apiFetch: ProgressApiFetch,
): Promise<BaselineRow[]> {
  const baselineResp = await apiFetch<ListResp>(
    "/api/session-results/baselines",
  );
  const parsed: BaselineRow[] = [];
  for (const raw of baselineResp.items ?? []) {
    if (!raw || typeof raw !== "object") continue;
    const r = raw as Record<string, unknown>;
    if (typeof r["muscleSlug"] !== "string") continue;
    const canonical = toCatalogSlug(r["muscleSlug"]);
    if (canonical === null) continue;
    parsed.push({
      muscleSlug: canonical,
      avg7dKg: Number(r["avg7dKg"] ?? 0),
      avg28dKg: Number(r["avg28dKg"] ?? 0),
      baselineKg: Number(r["baselineKg"] ?? 0),
      sampleCountDays: Number(r["sampleCountDays"] ?? 0),
    });
  }
  return parsed;
}

// Baselines SWR: paint the last cached rows immediately (mobile AsyncStorage,
// web localStorage — both inject a disk read), refresh behind.
function useBaselines(deps: ProgressDeps) {
  const [value, setValue] = useState<BaselineRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const apiRef = useRef(deps.apiFetch);
  const staleRef = useRef(deps.readStaleBaselines);
  const persistRef = useRef(deps.persistBaselines);
  useEffect(() => {
    apiRef.current = deps.apiFetch;
    staleRef.current = deps.readStaleBaselines;
    persistRef.current = deps.persistBaselines;
  });
  useEffect(() => {
    let cancelled = false;
    if (staleRef.current) {
      void staleRef.current().then((cached) => {
        if (cancelled || !cached) return;
        setValue((cur) => cur ?? [...cached]);
      });
    }
    void fetchBaselines(apiRef.current)
      .then((rows) => {
        if (cancelled) return;
        setValue(rows);
        persistRef.current?.(rows);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "load_failed");
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return { baselines: value, error };
}

const dedupBySlug = <T extends { exerciseSlug?: string }>(rows: T[]): T[] => {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const r of rows) {
    if (!r.exerciseSlug) continue;
    if (seen.has(r.exerciseSlug)) continue;
    seen.add(r.exerciseSlug);
    out.push(r);
  }
  return out;
};

export function AlertsPanel(deps: ProgressDeps) {
  const { theme, painMap } = deps;
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const { results, catalog, error: srError } = useSessionResultsFromDeps(deps);
  const { baselines, error: blError } = useBaselines(deps);
  const error = srError ?? blError ?? null;
  const getName = useExerciseNameResolver(catalog);

  // `evaluable` answers a different question from `alerts.length`: did ANY of
  // the three signals have enough data to reach a verdict at all? Without it
  // "nothing to flag" is printed identically to "we never looked", which is
  // the empty-vs-unresolved collapse. Computed inside this same pass — the
  // loops below already walk exactly the data the answer needs, and a second
  // walk would restate the same thresholds somewhere they could drift.
  const { alerts, evaluable } = useMemo<{
    alerts: Alert[];
    evaluable: boolean;
  }>(() => {
    if (!results || !baselines) return { alerts: [], evaluable: false };
    const nowMs = Date.now();
    const timedResults: TimedSessionResult[] = results;
    const out: Alert[] = [];
    // Set by the loops below as each signal proves it had enough data.
    let baselineEvaluable = false;
    let regressionEvaluable = false;

    // catalog slug → first non-green pain severity among the bodymap
    // regions it maps to. painMap stays in bodymap vocabulary; the volume
    // math is catalog vocab and bridges at this join only.
    const painForCatalogSlug = (
      slug: CatalogMuscleSlug,
    ): PainSeverity | null => {
      const targets = mapCatalogToCanonical(slug);
      for (const tSlug of targets) {
        const sev = painMap[tSlug as MuscleSlug];
        if (sev && sev !== "green") return sev;
      }
      return null;
    };

    // 1) Baseline-spike alerts — only "alarm" tier (≥+60%) gets a card.
    for (const b of baselines) {
      if (b.sampleCountDays < BASELINE_MIN_SAMPLE_DAYS) continue;
      if (b.baselineKg <= 0) continue;
      // Both guards passed, so this row could have produced a verdict —
      // a zero baseline is not "no spike", it is nothing to compare to.
      baselineEvaluable = true;
      const cmp = compareToBaseline(b.avg28dKg, b.baselineKg);
      if (cmp.severity === "alarm") {
        const label = t(muscleKey(b.muscleSlug));
        out.push({
          id: `baseline-spike-${b.muscleSlug}`,
          severity: "alarm",
          text: t("mobile.progress.alerts.baselineSpike", {
            defaultValue:
              "{{muscle}} volume +{{pct}}% vs 28d baseline — consider backing off",
            muscle: label,
            pct: Math.round(cmp.pctDelta * 100),
          }),
        });
      }
    }

    // 2) Cross-reference with painMap — escalate a spike on a red/yellow
    // muscle (the highest-value safety signal the dashboard ships).
    for (const b of baselines) {
      if (b.sampleCountDays < BASELINE_MIN_SAMPLE_DAYS) continue;
      const sev = painForCatalogSlug(b.muscleSlug);
      if (!sev) continue;
      const cmp = compareToBaseline(b.avg28dKg, b.baselineKg);
      // This loop gates only on sampleCountDays, so it looks like it could
      // emit for a row loop 1 skipped on `baselineKg <= 0` — i.e. an alert
      // with `baselineEvaluable` still false. It can't: compareToBaseline
      // returns severity "ok" whenever baselineKg <= 0 (progressTierOne.ts),
      // and "ok" continues here. Any row reaching past this line therefore
      // had baselineKg > 0 and was already marked evaluable by loop 1.
      if (cmp.severity === "ok") continue;
      const existingIdx = out.findIndex(
        (a) => a.id === `baseline-spike-${b.muscleSlug}`,
      );
      const label = t(muscleKey(b.muscleSlug));
      const painAlert: Alert = {
        id: `pain-volume-${b.muscleSlug}`,
        severity: "alarm",
        text: t("mobile.progress.alerts.painVolume", {
          defaultValue:
            "{{muscle}} flagged {{severity}} + volume +{{pct}}% vs baseline — back off",
          muscle: label,
          severity: t(`mobile.progress.painSeverity.${sev}`),
          pct: Math.round(cmp.pctDelta * 100),
        }),
      };
      if (existingIdx >= 0) out[existingIdx] = painAlert;
      else out.push(painAlert);
    }

    // 3) Regression alerts — top 5 lifts. Name resolved via FK→catalog.
    // `timedResults` is already the window this panel cares about, so the
    // funnel needs no bounds; the points come back chronological.
    // EQ-1-HIST — one candidate per (exercise, machine), which is what the
    // funnel now hands back. A decline is a decline ON A MACHINE: measuring it
    // across two machines would report a drop that is really a change of
    // equipment, which is the false positive the equipment model exists to
    // remove. The threshold below is untouched — a machine has to clear the
    // same MIN_REGRESSION_POINTS as an exercise ever did.
    const ranked = Array.from(e1rmPointsByTrack(timedResults).entries())
      .map(([key, v]) => {
        const latest = v.points[v.points.length - 1]?.kgPerRep1 ?? 0;
        return {
          key,
          name: exerciseTrackLabel(
            getName(v.slug, v.snapshot),
            v.equipmentInstance,
            t,
          ),
          points: v.points,
          latest,
        };
      })
      .sort((a, b) => b.latest - a.latest)
      .slice(0, 5);
    for (const r of ranked) {
      const reg = detectRegression(r.points, nowMs, 12);
      // Enough points to judge the trend, whichever way it came out.
      if (reg.pointCount >= MIN_REGRESSION_POINTS) regressionEvaluable = true;
      if (reg.flag === "regressing" && reg.pointCount >= MIN_REGRESSION_POINTS) {
        out.push({
          id: `regress-${r.key}`,
          severity: "warn",
          text: t("mobile.progress.alerts.regression", {
            defaultValue:
              "{{exercise}} e1RM {{pct}}% over {{n}} sessions — trending down",
            exercise: r.name,
            pct: Math.round(reg.pctChange * 100),
            n: reg.pointCount,
          }),
        });
      }
    }

    // 4) Deload suggestion — a single high-value signal, rendered inline.
    const deload = suggestDeload(timedResults, nowMs);
    if (deload.shouldDeload) {
      out.push({
        id: "deload",
        severity: "info",
        text: t("mobile.progress.alerts.deload", {
          defaultValue: "Consider a deload — {{reason}}",
          reason: deload.reason,
        }),
      });
    }

    // The deload leg reports its own sufficiency: `suggestDeload` only judges
    // the trend once the top lift clears MIN_TOPLIFT_REGRESSION_POINTS, and
    // it hands back the RegressionResult it used, so we read that rather than
    // restating the threshold. Its volume leg is deliberately NOT counted:
    // with no prior 4-week block `volumeChangePct` is 0, which is
    // indistinguishable from a genuinely flat 4 weeks, so it cannot tell us
    // whether it was evaluable.
    const deloadEvaluable =
      deload.topLiftRegression !== null &&
      deload.topLiftRegression.pointCount >= MIN_TOPLIFT_REGRESSION_POINTS;

    return {
      alerts: out,
      evaluable: baselineEvaluable || regressionEvaluable || deloadEvaluable,
    };
  }, [results, baselines, painMap, t, getName]);

  const alertsTitle = t("mobile.progress.alerts.title", {
    defaultValue: "Alerts",
  });
  const alertsHelp = t("mobile.progress.alerts.help", {
    defaultValue:
      'The decision-driving panel. Combines three signals: e1RM regression on your top movement patterns (12-wk window), per-muscle volume spike vs your 90-day trimmed baseline (alarm at +60%), and a deload suggestion when volume is rising while top-lift e1RM stays flat. Muscles flagged red/yellow in painMap upgrade their spike alert to "back off".',
  });

  // Only surface the error card when nothing cached is left to show (a
  // background refresh can fail under SWR while data still holds).
  if (error && (results === null || baselines === null)) {
    return (
      <View style={[styles.card, styles.cardError]}>
        <PanelTitleRow theme={theme} title={alertsTitle} body={alertsHelp} />
        <Text style={styles.errorText}>
          {t("mobile.progress.alerts.loadFailed", {
            defaultValue: "Couldn’t load alerts",
          })}
        </Text>
      </View>
    );
  }

  if (results === null || baselines === null) {
    return (
      <View style={styles.card}>
        <PanelTitleRow theme={theme} title={alertsTitle} body={alertsHelp} />
        <ActivityIndicator color={theme.palette.brand500} />
      </View>
    );
  }

  // Four states kept distinct, never collapsed (empty-vs-unresolved), same
  // discipline as TargetHistoryList: failed read, not-yet-loaded read,
  // loaded-but-nothing-was-measurable, and only then a real all-clear.
  // "Nothing to flag" is a verdict, and it must not be printed when no
  // signal had enough history to reach one.
  if (alerts.length === 0 && !evaluable) {
    return (
      <View style={styles.card}>
        <PanelTitleRow theme={theme} title={alertsTitle} body={alertsHelp} />
        <Text style={styles.allClear}>
          {t("mobile.progress.alerts.needMoreData", {
            // Promises EVALUATION, not an alert. Clearing the data floor
            // usually lands on the all-clear branch below, so "alerts will
            // appear" would be the same overstatement this panel exists to
            // stop making.
            defaultValue:
              "Not enough history yet — keep logging and this panel will start evaluating",
          })}
        </Text>
      </View>
    );
  }

  if (alerts.length === 0) {
    return (
      <View style={styles.card}>
        <PanelTitleRow theme={theme} title={alertsTitle} body={alertsHelp} />
        <Text style={styles.allClear}>
          {t("mobile.progress.alerts.allClear", {
            defaultValue: "Nothing to flag — keep training",
          })}
        </Text>
      </View>
    );
  }

  const dedupedAlerts = dedupBySlug(
    alerts.map((a) => ({ ...a, exerciseSlug: a.id })),
  );

  return (
    <View style={styles.card}>
      <PanelTitleRow theme={theme} title={alertsTitle} body={alertsHelp} />
      {dedupedAlerts.map((a) => (
        <View
          key={a.id}
          style={[
            styles.alertRow,
            a.severity === "alarm" && styles.alertRowAlarm,
            a.severity === "warn" && styles.alertRowWarn,
            a.severity === "info" && styles.alertRowInfo,
          ]}
        >
          {a.severity === "warn" ? (
            // "warn" gets its own glyph (a triangle, not the exclamation
            // "alarm" uses) — same colour+icon pairing DensityTrendPanel
            // uses for its trend arrow, so severity never reads by colour
            // alone (DS-24-alerts).
            <View style={styles.alertGlyphIcon}>
              <Icon
                name="warn"
                size={theme.typography.sizes.md}
                color={theme.palette.warn}
                label={t("mobile.progress.a11y.severityWarn")}
              />
            </View>
          ) : (
            <Text
              style={[
                styles.alertGlyph,
                {
                  color:
                    a.severity === "alarm"
                      ? theme.palette.danger
                      : theme.palette.ink2,
                },
              ]}
              accessibilityLabel={t(
                a.severity === "alarm"
                  ? "mobile.progress.a11y.severityAlarm"
                  : "mobile.progress.a11y.severityInfo",
              )}
            >
              {a.severity === "alarm" ? "!" : "i"}
            </Text>
          )}
          <Text
            style={[
              styles.alertText,
              a.severity === "alarm" && styles.alertTextAlarm,
            ]}
          >
            {a.text}
          </Text>
        </View>
      ))}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    card: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      padding: s.s4,
      gap: s.s3,
    },
    cardError: { borderColor: p.danger },
    alertRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s3,
      borderWidth: 1,
      borderColor: p.border,
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
      borderRadius: r.r1,
      backgroundColor: p.bg,
    },
    alertRowAlarm: {
      borderColor: p.danger,
      backgroundColor: p.dangerFill,
    },
    alertRowWarn: { borderColor: p.warn },
    alertRowInfo: { borderColor: p.border },
    alertGlyph: {
      fontFamily: ty.fonts.mono,
      fontSize: ty.sizes.md,
      fontWeight: "700",
      minWidth: 16,
      textAlign: "center",
    },
    alertGlyphIcon: {
      minWidth: 16,
      alignItems: "center",
      justifyContent: "center",
    },
    alertText: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontWeight: "600",
      lineHeight: ty.sizes.sm + 6,
      flex: 1,
      minWidth: 0,
    },
    alertTextAlarm: { color: p.danger },
    allClear: {
      color: p.ink3,
      fontSize: ty.sizes.sm,
      fontStyle: "italic",
    },
    errorText: {
      color: p.danger,
      fontSize: ty.sizes.sm,
    },
  });
}
